import { Buildings, ChartLineUp, ClipboardText, CreditCard, CurrencyCircleDollar, GearSix, Headset, House, Lightning, ListChecks, PlugsConnected, Receipt, SignOut, UserCircle, UsersThree, type Icon } from '@phosphor-icons/react';
import { PLATFORM_ROLE_LABEL } from '@brechoflow/contracts';
import { Suspense } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformRole } from '@/features/auth/guards';
import { useSession } from '@/features/auth/session';
import { LegalLinks } from '@/features/legal/legal-links';
import type { NavGroup } from '@/layouts/nav';
import { Shell, SidebarGroups, TopBarFrame, UserMenuHeader, UserMenuTrigger } from '@/layouts/shell';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { navFor } from './lib';

const ICONS: Record<string, Icon> = {
  '/plataforma': House,
  '/plataforma/organizacoes': Buildings,
  '/plataforma/usuarios': UsersThree,
  '/plataforma/suporte': Headset,
  '/plataforma/assinaturas': ListChecks,
  '/plataforma/planos': CreditCard,
  '/plataforma/cobranca': Receipt,
  '/plataforma/uso': Lightning,
  '/plataforma/metricas': ChartLineUp,
  '/plataforma/auditoria': ClipboardText,
  '/plataforma/servicos': PlugsConnected,
  '/plataforma/configuracoes': GearSix,
};

const GROUPS: { label?: string; to: string[] }[] = [
  { label: 'Plataforma', to: ['/plataforma', '/plataforma/organizacoes', '/plataforma/usuarios', '/plataforma/suporte'] },
  { label: 'Gestão', to: ['/plataforma/assinaturas', '/plataforma/planos', '/plataforma/cobranca', '/plataforma/uso', '/plataforma/metricas'] },
  { label: 'Sistema', to: ['/plataforma/auditoria', '/plataforma/servicos', '/plataforma/configuracoes'] },
];

function PlatformNav({ onNavigate }: { onNavigate?: () => void }) {
  const role = usePlatformRole();
  const entries = navFor(role);
  const groups: NavGroup[] = GROUPS.map((g) => ({
    label: g.label,
    items: entries.filter((e) => g.to.includes(e.to)).map((e) => ({ label: e.label, to: e.to, icon: ICONS[e.to] ?? CurrencyCircleDollar, end: e.to === '/plataforma' })),
  })).filter((g) => g.items.length > 0);
  return <SidebarGroups groups={groups} label="Plataforma" onNavigate={onNavigate} />;
}

function OperatorFooter({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useSession();
  if (!me) return null;
  return (
    <NavLink
      to="/plataforma/conta"
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'mt-auto flex min-h-12 items-center gap-3 rounded-control border-t border-sidebar-border px-3 pt-2 text-sm font-medium transition-colors',
          isActive ? 'text-white dark:text-sidebar-foreground' : 'text-sidebar-foreground/85 hover:text-white dark:hover:text-sidebar-foreground',
        )
      }
    >
      <UserCircle className="size-[18px] shrink-0 text-sidebar-muted" aria-hidden />
      <span className="min-w-0">
        <span className="block">Minha conta</span>
        <span className="block truncate text-xs font-normal text-sidebar-muted">{me.user.email}</span>
      </span>
    </NavLink>
  );
}

function PlatformTopBar({ onMenu }: { onMenu: () => void }) {
  const { me, logout } = useSession();
  const role = usePlatformRole();
  const navigate = useNavigate();
  if (!me) return null;
  const onLogout = async () => {
    await logout();
    navigate('/entrar', { replace: true });
  };
  return (
    <TopBarFrame onMenu={onMenu}>
      <div className="flex min-w-0 items-center gap-2 px-2 sm:px-3">
        <span className="truncate font-display text-base font-semibold">Plataforma</span>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Super admin</span>
      </div>
      <div className="ml-auto" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <UserMenuTrigger name={me.user.name} initialsText={initials(me.user.name)} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-1rem)]">
          <UserMenuHeader name={me.user.name} email={me.user.email} role={role ? PLATFORM_ROLE_LABEL[role] : ''} />
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/plataforma/conta">
              <UserCircle className="size-4" aria-hidden />
              Minha conta
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void onLogout()}>
            <SignOut className="size-4" aria-hidden />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TopBarFrame>
  );
}

export function PlatformLayout() {
  return (
    <Shell
      brandTo="/plataforma"
      testId="platform-layout"
      menuTitle="Menu da plataforma"
      menuDescription="Seções do admin da plataforma"
      nav={(onNavigate) => <PlatformNav onNavigate={onNavigate} />}
      footer={(onNavigate) => (
        <>
          <OperatorFooter onNavigate={onNavigate} />
          <LegalLinks onNavigate={onNavigate} className="px-3 text-sidebar-muted" linkClassName="inline-flex min-h-8 items-center hover:text-white dark:hover:text-sidebar-foreground" />
        </>
      )}
      topBar={(onMenu) => <PlatformTopBar onMenu={onMenu} />}
    >
      <Suspense
        fallback={
          <div role="status" aria-label="Carregando" className="flex flex-col gap-3">
            <Skeleton className="h-10 max-w-md" />
            <Skeleton className="h-10" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </Shell>
  );
}
